import {
  Component,
  Inject,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Page, SummaryItem, Question, Client } from '../../models';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { debounceTime, ReplaySubject, takeUntil } from 'rxjs';
import {
  trigger,
  state,
  style,
  transition,
  animate,
  keyframes,
} from '@angular/animations';
import { FormView } from '@xilo-mono/form-contracts';
import { CustomerFormFacade, RepeatTitlePipe } from '@xilo-mono/form-viewer';
import { isNumber } from 'lodash';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  animations: [
    trigger('ShowMoreAnimation', [
      state('void', style({ opacity: 0, transform: 'translateY(0px)' })),
      state('*', style({ opacity: 1, transform: 'translateY(0)' })),
      transition(
        ':enter',
        animate(
          '300ms ease-out',
          keyframes([
            style({ opacity: 0, transform: 'translateY(0px)', offset: 0 }),
            style({ opacity: 1, transform: 'translateY(0)', offset: 1 }),
          ])
        )
      ),
      transition(':leave', animate('100ms ease-in')),
    ]),
  ],
})
export class SummaryComponent implements OnInit, OnDestroy {
  @Input() mainForm: FormGroup;
  @Input() page: Page;
  @Input() client: Client;
  @Input() formData: FormView;
  @Input() isMobile: boolean;
  @Input() summary: SummaryItem[];
  @Input() isLastQuestion: boolean;
  @Input() question: Question;
  @Input() currentForm: FormGroup;
  @Input() model: any = {};

  @Output()
  createNewFormQuestionTrigger: EventEmitter<string> = new EventEmitter();

  @Output()
  deleteMultipleItemTrigger: EventEmitter<SummaryItem> = new EventEmitter();

  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  currentExpanded;

  sectionIndex = 0;
  subsectionIndex: number;
  sectionProgress = 0;
  questionProgress = 0;
  subsectionProgress = 0;
  progressSummary: any;
  formIsLoaded = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private customerFormFacade: CustomerFormFacade,
    @Inject(RepeatTitlePipe) private repeatTitlePipe:RepeatTitlePipe
  ) {}

  ngOnInit(): void {
    // Subscribe to form value changes with debounce
    this.mainForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        this.formIsLoaded = true;
        // Add labels to all fields in the form
        if (this.formData?.components?.[0]?.fieldGroup) {
          this.formData.components[0].fieldGroup.forEach(async (summaryItem, index) => {
            try {
              if (summaryItem.fieldArray) {
                // Handle repeatable sections
                const values = summaryItem.fieldArray.fieldGroup || [];
                for (let subIndex = 0; subIndex < values.length; subIndex++) {
                  await this.setLabel(summaryItem, subIndex, true);
                }
              } else {
                // Handle regular sections
                await this.setLabel(summaryItem, 0, false);
              }
              this.cdr.detectChanges();
            } catch (error) {
              console.error('Error setting field label', error);
            }
          });
        }
      });

    this.customerFormFacade.sectionIndex$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((index) => {
        this.sectionIndex = index;
      });
    this.customerFormFacade.progressSummary$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((progress) => {
        this.progressSummary = progress;
        this.sectionProgress = progress.sectionIndex;
        this.questionProgress = progress.questionIndex;
        this.subsectionProgress = progress.subsectionIndex;
      });
    this.customerFormFacade.subsectionIndex$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((index) => {
        this.subsectionIndex = index;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  async setLabel(section: FormlyFieldConfig, subIndex: number, hasRepeats: boolean): Promise<void> {
    try {
      if (!section) {
        console.warn('setLabel: section is null or undefined');
        return;
      }
      let cachedLabel;
      if (hasRepeats) {
        const field = section.fieldArray;
        if (!field) {
          console.warn('setLabel: fieldArray is null or undefined for repeated section');
          return;
        }
        const baseLabel = section.templateOptions?.label || 'Section';
        const transformedLabel = this.repeatTitlePipe?.transform(
          baseLabel,
          field,
          field.templateOptions,
          subIndex
        );
        cachedLabel = transformedLabel || `${baseLabel} ${subIndex + 1}`;
        const repeatItem = field.fieldGroup?.[subIndex];
        if (repeatItem) {
          if (!repeatItem.templateOptions) {
            repeatItem.templateOptions = {};
          }
          repeatItem.templateOptions.cachedLabel = cachedLabel;
        } else {
          console.warn(`setLabel: No repeat item found at index ${subIndex}`);
        }
      } else {
        cachedLabel = section.templateOptions?.label || 'Section';
        if (!section.templateOptions) {
          section.templateOptions = {};
        }
        section.templateOptions.cachedLabel = cachedLabel;
      }
    } catch (error) {
      console.error('Error setting label:', error);
    }
  }

  summaryItemValid(summaryItem: any, subsection: number) {
    const formControl: AbstractControl = summaryItem.formControl;

    if (isNumber(subsection)) {
      const subControl = (<FormArray>formControl).controls[subsection];
      return subControl?.valid && !subControl?.pristine;
    } else {
      return formControl?.valid && !formControl?.pristine;
    }
  }

  showProgressIndicator(summaryItem: any, index: number, subsection: number) {
    if (summaryItem?.fieldArray) {
      return (
        index === this.sectionProgress &&
        (index !== this.sectionIndex || subsection > this.subsectionIndex)
      );
    } else {
      return index !== this.sectionIndex && index === this.sectionProgress;
    }
  }

  handlePageChange(section: number, subsection = 0) {
    this.customerFormFacade.setSectionAndQuestion(section, 0, subsection);
  }

  resumeProgress() {
    this.customerFormFacade.setSectionAndQuestion(
      this.sectionProgress,
      this.questionProgress,
      this.subsectionProgress
    );
  }

  isCurrentPageInProgress(
    summaryItem: any,
    index: number,
    repeatIndex
  ): boolean {
    if (summaryItem?.fieldArray) {
      return (
        index === this.sectionIndex && repeatIndex === this.subsectionIndex
      );
    } else {
      return index === this.sectionIndex;
    }
  }

  checkVisited(sectionIndex: number) {
    return sectionIndex <= this.sectionProgress;
  }

  removeItem(sectionIndex: number, repeatIndex: number) {
    if (confirm('Are you sure want to delete this?')) {
      this.customerFormFacade.triggerRemoveRepeatSection(
        sectionIndex,
        repeatIndex
      );
    }
  }
}
